import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { authAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Activity, ArrowRight, Eye, EyeOff, ArrowLeft, Mail } from "lucide-react";
import { toast } from "sonner";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("login");
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);

  // Verification state
  const [verifyStep, setVerifyStep] = useState(false);
  const [verifyEmail, setVerifyEmail] = useState("");
  const [verifyCode, setVerifyCode] = useState("");
  const [resending, setResending] = useState(false);

  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [regForm, setRegForm] = useState({
    tenant_name: "",
    name: "",
    email: "",
    password: "",
    phone: "",
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(loginForm.email, loginForm.password);
      toast.success("Bienvenido de vuelta");
      navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al iniciar sesion");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (regForm.password.length < 6) {
      toast.error("La contrasena debe tener al menos 6 caracteres");
      return;
    }
    setLoading(true);
    try {
      const res = await authAPI.register(regForm);
      if (res.data.needs_verification) {
        setVerifyEmail(res.data.email);
        setVerifyStep(true);
        toast.success("Revisa tu email para el codigo de verificacion");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al registrarse");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await authAPI.verifyCode({ email: verifyEmail, code: verifyCode });
      const { token, user, tenant } = res.data;
      localStorage.setItem("fisio_token", token);
      localStorage.setItem("fisio_user", JSON.stringify(user));
      localStorage.setItem("fisio_tenant", JSON.stringify(tenant));
      toast.success("Cuenta verificada exitosamente");
      // Small delay to let auth context pick up the token
      window.location.href = "/";
    } catch (err) {
      toast.error(err.response?.data?.detail || "Codigo invalido");
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setResending(true);
    try {
      await authAPI.resendCode({ email: verifyEmail });
      toast.success("Nuevo codigo enviado a tu email");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al reenviar codigo");
    } finally {
      setResending(false);
    }
  };

  // Verification code screen
  if (verifyStep) {
    return (
      <div className="min-h-screen flex" data-testid="verify-page">
        <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
          <img
            src="https://images.pexels.com/photos/5793918/pexels-photo-5793918.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt="Fisioterapia"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
          <div className="relative z-10 flex flex-col justify-end p-10 pb-14">
            <div className="flex items-center gap-3 mb-4">
              <Activity className="w-8 h-8 text-white" />
              <span className="text-2xl font-bold text-white tracking-tight">Fisiotherapy.app</span>
            </div>
            <p className="text-white/80 text-lg max-w-md leading-relaxed">
              La plataforma que organiza tu practica clinica alrededor del episodio de atencion, dando continuidad terapeutica entre sesiones.
            </p>
          </div>
        </div>

        <div className="flex-1 flex items-center justify-center p-6 bg-background">
          <div className="w-full max-w-md">
            <div className="flex items-center gap-2 mb-8 lg:hidden">
              <Activity className="w-7 h-7 text-primary" />
              <span className="text-xl font-bold text-foreground">Fisiotherapy.app</span>
            </div>

            <Card className="border-0 shadow-none bg-transparent">
              <CardHeader className="px-0 pt-0 text-center">
                <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                  <Mail className="w-8 h-8 text-primary" />
                </div>
                <CardTitle className="text-2xl">Verifica tu email</CardTitle>
                <CardDescription>
                  Enviamos un codigo de 6 digitos a<br />
                  <span className="font-medium text-foreground">{verifyEmail}</span>
                </CardDescription>
              </CardHeader>
              <CardContent className="px-0">
                <form onSubmit={handleVerifyCode} className="space-y-4">
                  <div>
                    <Label htmlFor="verify-code">Codigo de verificacion</Label>
                    <Input
                      id="verify-code"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={6}
                      placeholder="000000"
                      value={verifyCode}
                      onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      className="text-center text-2xl tracking-[0.5em] font-mono"
                      required
                      autoFocus
                      data-testid="verify-code-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full rounded-full"
                    disabled={loading || verifyCode.length !== 6}
                    data-testid="verify-submit"
                  >
                    {loading ? "Verificando..." : "Verificar cuenta"}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </form>

                <div className="mt-6 text-center space-y-3">
                  <p className="text-sm text-muted-foreground">
                    No recibiste el codigo?
                  </p>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleResendCode}
                    disabled={resending}
                    data-testid="resend-code-btn"
                  >
                    {resending ? "Reenviando..." : "Reenviar codigo"}
                  </Button>
                  <div>
                    <Button
                      variant="link"
                      size="sm"
                      onClick={() => { setVerifyStep(false); setVerifyCode(""); }}
                      className="text-muted-foreground"
                    >
                      <ArrowLeft className="w-3 h-3 mr-1" />
                      Volver al registro
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="https://images.pexels.com/photos/5793918/pexels-photo-5793918.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="Fisioterapia"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
        <div className="relative z-10 flex flex-col justify-end p-10 pb-14">
          <div className="flex items-center gap-3 mb-4">
            <Activity className="w-8 h-8 text-white" />
            <span className="text-2xl font-bold text-white tracking-tight">Fisiotherapy.app</span>
          </div>
          <p className="text-white/80 text-lg max-w-md leading-relaxed">
            La plataforma que organiza tu practica clinica alrededor del episodio de atencion, dando continuidad terapeutica entre sesiones.
          </p>
        </div>
      </div>

      {/* Right - Form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-background">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <Activity className="w-7 h-7 text-primary" />
            <span className="text-xl font-bold text-foreground">Fisiotherapy.app</span>
          </div>

          <Tabs value={tab} onValueChange={setTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="login" data-testid="tab-login">Iniciar sesion</TabsTrigger>
              <TabsTrigger value="register" data-testid="tab-register">Crear cuenta</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <Card className="border-0 shadow-none bg-transparent">
                <CardHeader className="px-0 pt-0">
                  <CardTitle className="text-2xl">Bienvenido</CardTitle>
                  <CardDescription>Ingresa a tu cuenta para continuar</CardDescription>
                </CardHeader>
                <CardContent className="px-0">
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div>
                      <Label htmlFor="login-email">Email</Label>
                      <Input
                        id="login-email"
                        type="email"
                        placeholder="tu@email.com"
                        value={loginForm.email}
                        onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                        required
                        data-testid="login-email"
                      />
                    </div>
                    <div>
                      <Label htmlFor="login-password">Contrasena</Label>
                      <div className="relative">
                        <Input
                          id="login-password"
                          type={showPw ? "text" : "password"}
                          placeholder="Tu contrasena"
                          value={loginForm.password}
                          onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                          required
                          data-testid="login-password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPw(!showPw)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          data-testid="toggle-password"
                        >
                          {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <Button
                      type="submit"
                      className="w-full rounded-full"
                      disabled={loading}
                      data-testid="login-submit"
                    >
                      {loading ? "Ingresando..." : "Iniciar sesion"}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="register">
              <Card className="border-0 shadow-none bg-transparent">
                <CardHeader className="px-0 pt-0">
                  <CardTitle className="text-2xl">Crear cuenta</CardTitle>
                  <CardDescription>Registra tu clinica para empezar</CardDescription>
                </CardHeader>
                <CardContent className="px-0">
                  <form onSubmit={handleRegister} className="space-y-4">
                    <div>
                      <Label htmlFor="reg-clinic">Nombre de la clinica</Label>
                      <Input
                        id="reg-clinic"
                        placeholder="Mi Clinica de Fisioterapia"
                        value={regForm.tenant_name}
                        onChange={(e) => setRegForm({ ...regForm, tenant_name: e.target.value })}
                        required
                        data-testid="register-clinic"
                      />
                    </div>
                    <div>
                      <Label htmlFor="reg-name">Tu nombre completo</Label>
                      <Input
                        id="reg-name"
                        placeholder="Dr. Juan Perez"
                        value={regForm.name}
                        onChange={(e) => setRegForm({ ...regForm, name: e.target.value })}
                        required
                        data-testid="register-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="reg-email">Email</Label>
                      <Input
                        id="reg-email"
                        type="email"
                        placeholder="tu@email.com"
                        value={regForm.email}
                        onChange={(e) => setRegForm({ ...regForm, email: e.target.value })}
                        required
                        data-testid="register-email"
                      />
                    </div>
                    <div>
                      <Label htmlFor="reg-password">Contrasena</Label>
                      <Input
                        id="reg-password"
                        type="password"
                        placeholder="Minimo 6 caracteres"
                        value={regForm.password}
                        onChange={(e) => setRegForm({ ...regForm, password: e.target.value })}
                        required
                        data-testid="register-password"
                      />
                    </div>
                    <Button
                      type="submit"
                      className="w-full rounded-full"
                      disabled={loading}
                      data-testid="register-submit"
                    >
                      {loading ? "Creando cuenta..." : "Crear cuenta"}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
